import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import PageHero from "../../components/feature/PageHero";
import { useEffect } from "react";
import SEOHead from "../../components/feature/SEOHead";

export default function QuemSomosPage() {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Quem Somos - IBX Brasil",
    "description": "Conheça a IBX Brasil, consultoria especializada em seguros empresariais com mais de 15 anos de experiência em proteção patrimonial e gestão de riscos corporativos.",
    "url": "https://ibxbrasil.com.br/quem-somos",
    "mainEntity": {
      "@type": "Organization",
      "name": "IBX Brasil",
      "description": "Consultoria especializada em seguros empresariais",
      "foundingDate": "2008",
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "value": "25"
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Quem Somos - Especialistas em Seguros Empresariais | IBX Brasil"
        description="Conheça a IBX Brasil: mais de 15 anos de experiência em consultoria de seguros empresariais, com equipe especializada em proteção patrimonial e gestão de riscos."
        canonical="https://ibxbrasil.com.br/quem-somos"
        ogImage="https://readdy.ai/api/search-image?query=professional%20insurance%20consulting%20team%20in%20modern%20corporate%20office%20with%20expertise%20in%20business%20insurance%20and%20risk%20management&width=1200&height=630&seq=ibx-quem-somos-og&orientation=landscape"
        schema={webPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <PageHero
          badge="QUEM SOMOS"
          title="Décadas de Experiência em **Gestão de Riscos**"
          subtitle="A IBX Brasil é referência nacional em gestão de riscos e contratação profissional de seguros, com atuação consolidada no mercado e compromisso com a proteção patrimonial inteligente."
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20insurance%20consulting%20team%20modern%20elegant%20office%20interior%20deep%20teal%20petrol%20blue%20accents%20corporate%20environment%20business%20executives%20meeting%20sophisticated%20workspace%20natural%20lighting%20trust%20expertise&width=1920&height=1080&seq=ibxquemsomos-hero&orientation=landscape"
          primaryCta={{
            text: "Falar no WhatsApp",
            href: "https://wa.me/5577988080505"
          }}
          secondaryCta={{
            text: "Solicitar Análise Gratuita",
            href: "/contato"
          }}
          overlayIntensity="medium"
        />

        {/* História */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="w-full h-[550px] rounded-2xl overflow-hidden shadow-2xl bg-stone-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20insurance%20consulting%20office%20modern%20elegant%20workspace%20financial%20documents%20contracts%20handshake%20business%20meeting%20trust%20security%20wealth%20management%20sophisticated%20environment%20natural%20lighting%20warm%20tones&width=800&height=1100&seq=ibxhistory01&orientation=portrait"
                    alt="História da IBX Brasil - Consultoria em Seguros"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="1100"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-orange-100 rounded-2xl -z-10"></div>
              </div>

              <div>
                <span className="inline-block text-orange-500 text-sm font-semibold uppercase tracking-widest mb-4">
                  Nossa História
                </span>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Construída sobre <span className="text-orange-500">Conhecimento e Confiança</span>
                </h2>
                <p className="text-base text-gray-600 mb-5 leading-relaxed">
                  Há mais de duas décadas, a IBX Brasil iniciou sua trajetória no mercado de seguros com uma missão clara: profissionalizar a contratação de seguros no Brasil e oferecer proteção patrimonial real, não apenas apólices padronizadas.
                </p>
                <p className="text-base text-gray-600 mb-5 leading-relaxed">
                  Ao longo dos anos, acumulamos conhecimento profundo sobre o funcionamento das seguradoras, as práticas dos corretores, as armadilhas contratuais mais comuns e, principalmente, sobre como negociar condições verdadeiramente vantajosas para nossos clientes.
                </p>
                <p className="text-base text-gray-600 mb-5 leading-relaxed">
                  Nossa experiência nos permitiu desenvolver uma metodologia própria de análise de riscos e contratação de seguros, que combina expertise técnica, visão estratégica e poder de negociação para garantir economia financeira e segurança jurídica.
                </p>
                <p className="text-base text-gray-600 leading-relaxed">
                  Hoje, atendemos pessoas físicas e empresas de diversos portes e segmentos, sempre com o mesmo compromisso: entregar proteção real, transparência total e resultados mensuráveis.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Filosofia de Trabalho */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block text-orange-500 text-sm font-semibold uppercase tracking-widest mb-4">
                Nossa Filosofia
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Como <span className="text-orange-500">Trabalhamos</span>
              </h2>
              <p className="text-base text-gray-600 leading-relaxed">
                Nossa abordagem é fundamentada em três pilares que garantem resultados superiores na contratação de seguros.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-search-eye-line text-3xl text-orange-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Análise Detalhada
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Realizamos diagnóstico completo dos riscos reais de cada cliente, identificando exposições que frequentemente passam despercebidas em contratações convencionais. Cada análise é personalizada e considera o perfil específico do patrimônio a ser protegido.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-3xl text-orange-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Seleção Precisa
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Não oferecemos produtos prontos. Selecionamos coberturas específicas que realmente protegem contra os riscos identificados, eliminando coberturas desnecessárias e incluindo proteções essenciais frequentemente negligenciadas pelo mercado tradicional.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-hand-coin-line text-3xl text-orange-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Negociação Estratégica
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Negociamos diretamente com seguradoras, corretores e bancos para garantir as condições contratuais e financeiras mais vantajosas. Nossa experiência e volume de negociação nos permitem obter condições inacessíveis em contratações individuais.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Método de Contratação */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block text-orange-500 text-sm font-semibold uppercase tracking-widest mb-4">
                  Método IBX Brasil
                </span>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Contratação <span className="text-orange-500">Profissional</span> de Seguros
                </h2>
                <p className="text-base text-gray-600 mb-8 leading-relaxed">
                  Desenvolvemos uma metodologia exclusiva que transforma a contratação de seguros em um processo estratégico, técnico e orientado a resultados concretos.
                </p>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-xl flex items-center justify-center font-bold text-lg">
                      1
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Diagnóstico de Riscos</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Mapeamento completo das exposições patrimoniais, financeiras e operacionais do cliente.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-xl flex items-center justify-center font-bold text-lg">
                      2
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Análise Técnica</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Avaliação detalhada de apólices existentes, identificação de lacunas e oportunidades de melhoria.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-xl flex items-center justify-center font-bold text-lg">
                      3
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Estruturação de Coberturas</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Desenho personalizado de proteção, com coberturas adequadas aos riscos reais identificados.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-xl flex items-center justify-center font-bold text-lg">
                      4
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Negociação Especializada</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Negociação direta com seguradoras para obter condições contratuais e financeiras superiores.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-xl flex items-center justify-center font-bold text-lg">
                      5
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Acompanhamento Contínuo</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Gestão ativa dos contratos, suporte em sinistros e revisões periódicas para garantir proteção permanente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="w-full h-[650px] rounded-2xl overflow-hidden shadow-2xl bg-stone-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20insurance%20risk%20analysis%20financial%20planning%20documents%20charts%20graphs%20modern%20office%20consultant%20reviewing%20contracts%20strategic%20planning%20wealth%20protection%20elegant%20business%20setting%20natural%20light&width=800&height=1300&seq=ibxmethod01&orientation=portrait"
                    alt="Método de Contratação Profissional de Seguros IBX Brasil"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="1300"
                  />
                </div>
                <div className="absolute -top-8 -left-8 w-64 h-64 bg-orange-100 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Missão, Visão e Valores */}
        <section className="py-24 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block text-orange-400 text-sm font-semibold uppercase tracking-widest mb-4">
                Nossos Princípios
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Missão, Visão e <span className="text-orange-400">Valores</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-compass-3-line text-3xl text-orange-400"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Missão
                </h3>
                <p className="text-base text-gray-300 leading-relaxed">
                  Profissionalizar a contratação de seguros no Brasil, oferecendo análise técnica especializada, negociação estratégica e proteção patrimonial real para pessoas e empresas que buscam segurança e economia inteligente.
                </p>
              </div>

              <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-eye-line text-3xl text-orange-400"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Visão
                </h3>
                <p className="text-base text-gray-300 leading-relaxed">
                  Ser reconhecida como a principal referência nacional em gestão de riscos e contratação profissional de seguros, transformando a forma como pessoas e empresas protegem seu patrimônio no Brasil.
                </p>
              </div>

              <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-heart-3-line text-3xl text-orange-400"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Valores
                </h3>
                <ul className="space-y-3 text-base text-gray-300">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-400 mt-1 flex-shrink-0"></i>
                    <span>Transparência total em todas as negociações</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-400 mt-1 flex-shrink-0"></i>
                    <span>Excelência técnica e conhecimento especializado</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-400 mt-1 flex-shrink-0"></i>
                    <span>Compromisso com resultados mensuráveis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-400 mt-1 flex-shrink-0"></i>
                    <span>Ética e responsabilidade em cada contratação</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-400 mt-1 flex-shrink-0"></i>
                    <span>Personalização e atenção aos detalhes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais Competitivos */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block text-orange-500 text-sm font-semibold uppercase tracking-widest mb-4">
                Por Que Escolher a IBX Brasil
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Diferenciais <span className="text-orange-500">Competitivos</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-medal-line text-2xl text-orange-500"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Experiência Consolidada</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Mais de 20 anos de atuação no mercado de seguros brasileiro.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-user-star-line text-2xl text-orange-500"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Atendimento Personalizado</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Cada cliente recebe análise e estratégia exclusivas para seu perfil.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-2xl text-orange-500"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Resultados Mensuráveis</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Economia financeira comprovada e proteção patrimonial efetiva.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-star-line text-2xl text-orange-500"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Proteção Real</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Coberturas que realmente protegem, não apenas apólices padronizadas.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Pronto para Profissionalizar a Contratação dos Seus Seguros?
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Solicite uma análise gratuita e descubra como podemos proteger melhor seu patrimônio com economia inteligente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Falar no WhatsApp
              </a>
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Falar com Consultor
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}